import React from 'react';

import { Contact } from 'flow/types/Contact';
import UNKNOWN_AVATAR_SVG from 'images/ic_telavox_unknown-contact.svg';
import UserAvatar from 'flow/components/ui-components/UserAvatar';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Statuses } from 'flow/types/Extension';

import { TypedMap } from 'flow/types/TypedMap';
import { getFlowExtensionContactAvatar } from 'flow/utils';
import { AvatarSize } from './ChatAvatar';
import { getContactBackgroundColor } from 'flow/utils/extension-utils-TW';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectContact } from 'flow/selectors/ExtensionSelectors';

interface Props {
    extensionKey?: ExtensionKey;
    contact?: TypedMap<Contact>;
    size?: AvatarSize;
    status?: Statuses;
    ignoreAvatarResource?: boolean;
    lazyLoad?: boolean;
}

const ExtensionContactAvatar = ({
    extensionKey,
    contact,
    ignoreAvatarResource,
    size,
    status,
    lazyLoad,
}: Props) => {
    const resolvedContact = useAppSelector(
        (state) => contact || selectContact(state, extensionKey),
    );

    const standardProps = { status, size, lazyLoad };
    if (!resolvedContact) {
        // This case can happen when the contact no longer exists.
        return <UserAvatar src={UNKNOWN_AVATAR_SVG} {...standardProps} />;
    }

    if (resolvedContact.get('avatarResource') && !ignoreAvatarResource) {
        return (
            <UserAvatar
                src={getFlowExtensionContactAvatar(resolvedContact)}
                {...standardProps}
            />
        );
    }

    const initials = extractContactInitials(resolvedContact);
    if (initials) {
        return (
            <UserAvatar
                className={getContactBackgroundColor(resolvedContact)}
                {...standardProps}
            >
                {initials}
            </UserAvatar>
        );
    }

    // A contact carrying only a company name yields no initials, and an avatar with
    // neither image nor initials paints nothing at all. Same picture as no contact.
    return <UserAvatar src={UNKNOWN_AVATAR_SVG} {...standardProps} />;
};

/** The initials an avatar draws, from the names themselves — for a caller that
 *  holds the names but no contact to read them off. */
export const initialsFromNames = (firstName = '', lastName = '') =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

export const extractContactInitials = (contact: TypedMap<Contact>) =>
    initialsFromNames(
        contact.get('firstName', ''),
        contact.get('lastName', ''),
    );

export default ExtensionContactAvatar;
